import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLoader, FiAlertCircle, FiBarChart2, FiPieChart, FiRefreshCw, FiDownload, FiSearch, 
       FiChevronDown, FiChevronUp, FiMonitor, FiSmartphone, FiTablet, FiGlobe, FiCpu, 
       FiCalendar, FiMapPin } = FiIcons;

const AnalyticsView = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    devices: {},
    browsers: {},
    os: {},
    productTypes: {},
    orderTypes: {},
    ipAddresses: {},
    regions: {}
  });
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDevice, setFilterDevice] = useState('');
  const [filterBrowser, setFilterBrowser] = useState('');
  const [filterOs, setFilterOs] = useState('');

  useEffect(() => {
    fetchOrdersWithAnalytics();
  }, []);

  useEffect(() => {
    if (orders.length > 0) {
      calculateStats();
    }
  }, [orders]);

  const fetchOrdersWithAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('burger_orders_hb47x29k1a')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;

      // Also fetch IP data to enrich orders
      const { data: ipData, error: ipError } = await supabase
        .from('ip_analytics_simple')
        .select('*');

      if (ipError) {
        console.error('Error fetching IP data:', ipError);
      }

      // Create IP lookup map
      const ipMap = {};
      if (ipData && ipData.length > 0) {
        ipData.forEach(item => {
          if (item.order_id) {
            ipMap[item.order_id] = item.ip_address;
          }
        });
      }

      // Parse analytics data if it exists
      const ordersWithParsedAnalytics = data.map(order => {
        try {
          const analyticsData = order.analytics_data ? 
            (typeof order.analytics_data === 'string' ? JSON.parse(order.analytics_data) : order.analytics_data) : 
            {};
          
          // Enrich with IP address from separate table if available
          if (ipMap[order.id] && (!analyticsData.ipAddress || analyticsData.ipAddress === 'Wird vom Server ergänzt')) {
            analyticsData.ipAddress = ipMap[order.id];
          }

          return { ...order, analyticsData };
        } catch (e) {
          console.error(`Failed to parse analytics for order ${order.id}:`, e);
          return { ...order, analyticsData: {} };
        }
      });

      setOrders(ordersWithParsedAnalytics);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError('Fehler beim Laden der Bestellungen und Analysedaten');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = () => {
    const devices = {};
    const browsers = {};
    const operatingSystems = {};
    const productTypes = {};
    const orderTypes = {};
    const ipAddresses = {};
    const regions = {};

    orders.forEach(order => {
      // Device stats
      const deviceType = order.analyticsData?.deviceType || 'Unbekannt';
      devices[deviceType] = (devices[deviceType] || 0) + 1;

      // Browser stats
      const browserName = order.analyticsData?.browser?.name || 'Unbekannt';
      browsers[browserName] = (browsers[browserName] || 0) + 1;

      // OS stats
      const osName = order.analyticsData?.os?.name || 'Unbekannt';
      operatingSystems[osName] = (operatingSystems[osName] || 0) + 1;

      // Product type stats
      const productType = order.product_type || 'Unbekannt';
      productTypes[productType] = (productTypes[productType] || 0) + 1;

      // Order type stats
      const orderType = order.order_type || 'Unbekannt';
      orderTypes[orderType] = (orderTypes[orderType] || 0) + 1;

      // IP address stats
      const ipAddress = order.analyticsData?.ipAddress || 'Unbekannt';
      if (ipAddress !== 'Wird vom Server ergänzt' && ipAddress !== 'Unbekannt') {
        ipAddresses[ipAddress] = (ipAddresses[ipAddress] || 0) + 1;

        // Simple region detection based on IP (very basic)
        let region = 'Unbekannt';
        if (ipAddress.startsWith('192.168.') || ipAddress.startsWith('10.') || ipAddress.startsWith('172.')) {
          region = 'Lokales Netzwerk';
        } else if (ipAddress.startsWith('185.') || ipAddress.startsWith('212.')) {
          region = 'Schweiz';
        } else if (ipAddress.startsWith('91.') || ipAddress.startsWith('95.')) {
          region = 'Deutschland';
        } else if (ipAddress.startsWith('213.')) {
          region = 'Schweiz/Österreich';
        } else if (ipAddress.startsWith('84.') || ipAddress.startsWith('77.')) {
          region = 'Europa';
        } else {
          // Try to determine region from IP format
          region = 'Internationaler Besucher';
        }
        regions[region] = (regions[region] || 0) + 1;
      }
    });

    setStats({
      total: orders.length,
      devices,
      browsers,
      os: operatingSystems,
      productTypes,
      orderTypes,
      ipAddresses,
      regions
    });
  };

  const toggleOrderExpanded = (orderId) => {
    setExpandedOrderId(expandedOrderId === orderId ? null : orderId);
  };

  const exportAnalyticsCSV = () => {
    const headers = [
      'Bestell-ID', 'Kunde', 'Datum', 'Produkt', 'Bestelltyp', 'Gerät', 'Browser',
      'Betriebssystem', 'Bildschirmgröße', 'Fenster', 'Verbindung', 'Sprache',
      'Zeitzone', 'IP-Adresse'
    ];

    const rows = orders.map(order => [
      order.id.substring(0, 8),
      order.customer_name,
      new Date(order.created_at).toLocaleString('de-DE'),
      order.product_type,
      order.order_type,
      order.analyticsData?.deviceType || 'Unbekannt',
      `${order.analyticsData?.browser?.name || 'Unbekannt'} ${order.analyticsData?.browser?.version || ''}`,
      `${order.analyticsData?.os?.name || 'Unbekannt'} ${order.analyticsData?.os?.version || ''}`,
      order.analyticsData?.screenResolution ? `${order.analyticsData.screenResolution.width}x${order.analyticsData.screenResolution.height}` : 'Unbekannt',
      order.analyticsData?.windowSize ? `${order.analyticsData.windowSize.width}x${order.analyticsData.windowSize.height}` : 'Unbekannt',
      order.analyticsData?.connectionType || 'Unbekannt',
      order.analyticsData?.language || 'Unbekannt',
      order.analyticsData?.timezone || 'Unbekannt',
      order.analyticsData?.ipAddress || 'Unbekannt'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `wunschburger-analytics-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = searchQuery === '' || 
      order.customer_name.toLowerCase().includes(searchQuery.toLowerCase()) || 
      order.customer_email.toLowerCase().includes(searchQuery.toLowerCase()) || 
      order.id.toLowerCase().includes(searchQuery.toLowerCase()) || 
      (order.analyticsData?.ipAddress && order.analyticsData.ipAddress.includes(searchQuery));

    const matchesDevice = filterDevice === '' || order.analyticsData?.deviceType === filterDevice;
    const matchesBrowser = filterBrowser === '' || order.analyticsData?.browser?.name === filterBrowser;
    const matchesOs = filterOs === '' || order.analyticsData?.os?.name === filterOs;

    return matchesSearch && matchesDevice && matchesBrowser && matchesOs;
  });

  // Get unique values for filters
  const uniqueDevices = [...new Set(orders.map(order => order.analyticsData?.deviceType).filter(Boolean))];
  const uniqueBrowsers = [...new Set(orders.map(order => order.analyticsData?.browser?.name).filter(Boolean))];
  const uniqueOs = [...new Set(orders.map(order => order.analyticsData?.os?.name).filter(Boolean))];

  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center">
          <SafeIcon icon={FiBarChart2} className="mr-2 text-orange-500" />
          📊 Kunden-Analytics & Geräteanalyse
        </h1>
        <div className="flex space-x-2">
          <button
            onClick={fetchOrdersWithAnalytics}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center"
            disabled={loading}
          >
            <SafeIcon icon={loading ? FiLoader : FiRefreshCw} className={`mr-1 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </button>
          <button
            onClick={exportAnalyticsCSV}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center"
            disabled={loading || orders.length === 0}
          >
            <SafeIcon icon={FiDownload} className="mr-1" />
            CSV exportieren
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200 text-red-700">
          <div className="flex items-center">
            <SafeIcon icon={FiAlertCircle} className="mr-2 text-red-500" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Statistics Overview */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
        <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white p-4">
          <h2 className="text-xl font-bold">Statistik-Übersicht</h2>
          <p className="text-sm opacity-80">Basierend auf {stats.total} gesammelten Bestellungen</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Gerätetypen */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
              <h3 className="text-lg font-semibold text-blue-800 flex items-center mb-3">
                <SafeIcon icon={FiMonitor} className="mr-2" />
                Gerätetypen
              </h3>
              <div className="space-y-2">
                {Object.entries(stats.devices).map(([device, count]) => (
                  <div key={device} className="flex justify-between items-center">
                    <div className="flex items-center">
                      <SafeIcon 
                        icon={device === 'Desktop' ? FiMonitor : device === 'Mobilgerät' ? FiSmartphone : device === 'Tablet' ? FiTablet : FiGlobe} 
                        className="mr-2 text-blue-600" 
                      />
                      <span>{device}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="font-semibold mr-2">{count}</span>
                      <span className="text-sm text-gray-500">
                        ({Math.round((count / stats.total) * 100)}%)
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Browser */}
            <div className="bg-green-50 p-4 rounded-lg border border-green-100">
              <h3 className="text-lg font-semibold text-green-800 flex items-center mb-3">
                <SafeIcon icon={FiGlobe} className="mr-2" />
                Browser
              </h3>
              <div className="space-y-2">
                {Object.entries(stats.browsers).map(([browser, count]) => (
                  <div key={browser} className="flex justify-between items-center">
                    <span>{browser}</span>
                    <div className="flex items-center">
                      <span className="font-semibold mr-2">{count}</span>
                      <span className="text-sm text-gray-500">
                        ({Math.round((count / stats.total) * 100)}%)
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Betriebssysteme */}
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
              <h3 className="text-lg font-semibold text-purple-800 flex items-center mb-3">
                <SafeIcon icon={FiCpu} className="mr-2" />
                Betriebssysteme
              </h3>
              <div className="space-y-2">
                {Object.entries(stats.os).map(([os, count]) => (
                  <div key={os} className="flex justify-between items-center">
                    <span>{os}</span>
                    <div className="flex items-center">
                      <span className="font-semibold mr-2">{count}</span>
                      <span className="text-sm text-gray-500">
                        ({Math.round((count / stats.total) * 100)}%)
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Produkttypen */}
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-100">
              <h3 className="text-lg font-semibold text-orange-800 flex items-center mb-3">
                <SafeIcon icon={FiPieChart} className="mr-2" />
                Produkttypen
              </h3>
              <div className="space-y-2">
                {Object.entries(stats.productTypes).map(([type, count]) => (
                  <div key={type} className="flex justify-between items-center">
                    <span>{type === 'burger' ? '🍔 Burger' : type === 'pizza' ? '🍕 Pizza' : type}</span>
                    <div className="flex items-center">
                      <span className="font-semibold mr-2">{count}</span>
                      <span className="text-sm text-gray-500">
                        ({Math.round((count / stats.total) * 100)}%)
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Bestelltypen */}
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-100">
              <h3 className="text-lg font-semibold text-yellow-800 flex items-center mb-3">
                <SafeIcon icon={FiCalendar} className="mr-2" />
                Bestelltypen
              </h3>
              <div className="space-y-2">
                {Object.entries(stats.orderTypes).map(([type, count]) => (
                  <div key={type} className="flex justify-between items-center">
                    <span>{type === 'pickup' ? '🔄 Abholung' : type === 'delivery' ? '🚚 Lieferung' : type}</span>
                    <div className="flex items-center">
                      <span className="font-semibold mr-2">{count}</span>
                      <span className="text-sm text-gray-500">
                        ({Math.round((count / stats.total) * 100)}%)
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Regionen basierend auf IP */}
            <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-100">
              <h3 className="text-lg font-semibold text-indigo-800 flex items-center mb-3">
                <SafeIcon icon={FiMapPin} className="mr-2" />
                Regionen (IP-basiert)
              </h3>
              <div className="space-y-2">
                {Object.entries(stats.regions).map(([region, count]) => (
                  <div key={region} className="flex justify-between items-center">
                    <span>{region}</span>
                    <div className="flex items-center">
                      <span className="font-semibold mr-2">{count}</span>
                      <span className="text-sm text-gray-500">
                        ({Math.round((count / stats.total) * 100)}%)
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6 p-6">
        <h3 className="text-lg font-semibold mb-4">Filter & Suche</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Suche nach Name, E-Mail, ID oder IP..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Device filter */}
          <div>
            <select
              value={filterDevice}
              onChange={(e) => setFilterDevice(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">Alle Gerätetypen</option>
              {uniqueDevices.map(device => (
                <option key={device} value={device}>{device}</option>
              ))}
            </select>
          </div>

          {/* Browser filter */}
          <div>
            <select
              value={filterBrowser}
              onChange={(e) => setFilterBrowser(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">Alle Browser</option>
              {uniqueBrowsers.map(browser => (
                <option key={browser} value={browser}>{browser}</option>
              ))}
            </select>
          </div>

          {/* OS filter */}
          <div>
            <select
              value={filterOs}
              onChange={(e) => setFilterOs(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">Alle Betriebssysteme</option>
              {uniqueOs.map(os => (
                <option key={os} value={os}>{os}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-4 text-sm text-gray-500">
          {filteredOrders.length} von {orders.length} Bestellungen angezeigt
        </div>
      </div>

      {/* Orders List with Analytics */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Bestell-ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Kunde
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Datum
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Produkt
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Gerät
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  IP-Adresse
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Aktionen
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center">
                    <SafeIcon icon={FiLoader} className="animate-spin mx-auto text-orange-500 text-xl" />
                    <p className="mt-2 text-gray-500">Daten werden geladen...</p>
                  </td>
                </tr>
              ) : filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                    Keine Bestellungen gefunden.
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order) => (
                  <React.Fragment key={order.id}>
                    <tr className={`hover:bg-gray-50 ${expandedOrderId === order.id ? 'bg-blue-50' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        #{order.id.substring(0, 8)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div>{order.customer_name}</div>
                        <div className="text-xs text-gray-400">{order.customer_email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(order.created_at).toLocaleString('de-DE')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {order.product_type === 'burger' ? '🍔 Burger' : '🍕 Pizza'}
                        <div className="text-xs text-gray-400">
                          {order.order_type === 'pickup' ? 'Abholung' : 'Lieferung'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {order.analyticsData?.deviceType || 'Unbekannt'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {order.analyticsData?.ipAddress || 'Unbekannt'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <button
                          onClick={() => toggleOrderExpanded(order.id)}
                          className="text-blue-600 hover:text-blue-800"
                          title={expandedOrderId === order.id ? "Details ausblenden" : "Details anzeigen"}
                        >
                          <SafeIcon icon={expandedOrderId === order.id ? FiChevronUp : FiChevronDown} />
                        </button>
                      </td>
                    </tr>
                    {expandedOrderId === order.id && (
                      <tr className="bg-blue-50">
                        <td colSpan="7" className="px-6 py-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <h4 className="font-semibold text-blue-800">Geräte-Informationen:</h4>
                              <div className="bg-white p-3 rounded-lg shadow-sm">
                                <p><strong>Gerät:</strong> {order.analyticsData?.deviceType || 'Unbekannt'}</p>
                                <p><strong>Browser:</strong> {order.analyticsData?.browser?.name || 'Unbekannt'} {order.analyticsData?.browser?.version || ''}</p>
                                <p><strong>Betriebssystem:</strong> {order.analyticsData?.os?.name || 'Unbekannt'} {order.analyticsData?.os?.version || ''}</p>
                                <p><strong>Bildschirmgröße:</strong> {order.analyticsData?.screenResolution ? `${order.analyticsData.screenResolution.width}x${order.analyticsData.screenResolution.height}` : 'Unbekannt'}</p>
                                <p><strong>Fenster:</strong> {order.analyticsData?.windowSize ? `${order.analyticsData.windowSize.width}x${order.analyticsData.windowSize.height}` : 'Unbekannt'}</p>
                                <p><strong>Verbindung:</strong> {order.analyticsData?.connectionType || 'Unbekannt'}</p>
                              </div>
                            </div>
                            <div className="space-y-2">
                              <h4 className="font-semibold text-blue-800">Weitere Informationen:</h4>
                              <div className="bg-white p-3 rounded-lg shadow-sm">
                                <p><strong>IP-Adresse:</strong> {order.analyticsData?.ipAddress || 'Unbekannt'}</p>
                                <p><strong>Sprache:</strong> {order.analyticsData?.language || 'Unbekannt'}</p>
                                <p><strong>Zeitzone:</strong> {order.analyticsData?.timezone || 'Unbekannt'}</p>
                                <p><strong>Pixel-Verhältnis:</strong> {order.analyticsData?.pixelRatio || 'Unbekannt'}</p>
                                <p><strong>Touchpoints:</strong> {order.analyticsData?.touchPoints || 'Unbekannt'}</p>
                                <p><strong>Referrer:</strong> {order.analyticsData?.referrer || 'Direkt'}</p>
                                <p><strong>User-Agent:</strong> <span className="text-xs break-all">{order.analyticsData?.userAgent || 'Unbekannt'}</span></p>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h3 className="font-semibold text-blue-800 mb-2">📊 Analytics-Informationen:</h3>
        <ul className="text-sm text-blue-700 space-y-1 list-disc pl-5">
          <li>Die Analytics-Daten werden automatisch bei jeder Bestellung erfasst.</li>
          <li>IP-Adressen werden über externe Dienste erfasst (ipify.org und ipapi.co).</li>
          <li>Diese Daten sind nur für den Admin sichtbar und werden nicht mit dem Kunden geteilt.</li>
          <li>Die Daten helfen, das Nutzerverhalten zu verstehen und die App zu optimieren.</li>
          <li>Sie können die Daten nach verschiedenen Kriterien filtern und als CSV exportieren.</li>
          <li>Die gesammelten Daten enthalten keine persönlichen Identifikatoren außer den Bestellinformationen.</li>
        </ul>
      </div>
    </div>
  );
};

export default AnalyticsView;